import { Button } from '@strata/react-buttons';
import { FormSection } from '@strata/react-form-section';
import { Grid } from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { DetailLabel, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { ServicingRate } from '@strata/react-servicing-rate';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import { Routing } from '@strata/react-timeline';
import classNames from 'classnames';
import { getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';

import { PharmacyResults } from 'components/tools/testReferral/PharmacyResult';
import { isReferralRowDisabled } from 'services/gridHelpers';
import { AppRoutes } from 'services/routes';
import { routeResultsMap, Sections } from 'services/testReferralHelpers';
import { useGetAllSuppliersQuery } from 'store/api/referenceApi';

const columns = [
    {
        accessorKey: 'product_service_display_name',
        header: 'Drug',
        size: 120,
    },
    {
        accessorKey: 'therapeutic_category',
        header: 'Therapeutic Category',
        size: 120,
    },
    {
        accessorKey: 'referral_start',
        header: 'Referral Start',
        size: 120,
    },
    {
        accessorKey: 'friendly_id',
        header: 'Referral ID',
        size: 120,
    },
    {
        accessorKey: 'supplier_name',
        header: 'Servicing Supplier',
        size: 120,
    },
    {
        accessorKey: 'referral_status',
        header: 'Status',
        size: 145,
        enableSorting: false,
        cell: ({ getValue }) => <ReferralStatusIndicator status={getValue()} />,
    },
];

export const ResultsSection = ({ onClear }) => {
    const formik = useFormikContext();
    const navigate = useNavigate();

    const { data: allSuppliersData } = useGetAllSuppliersQuery();
    const { supplierMap } = allSuppliersData ?? {};

    const routeResultMapped =
        routeResultsMap[getIn(formik.values, `${Sections.RESULTS}.scenario.route_code`)];

    const payerIdSelected = getIn(
        formik.values,
        `${Sections.PATIENT_AND_PHARMACY}.healthPlan.payerId`,
    );
    const pharmacySelected = getIn(formik.values, `${Sections.PATIENT_AND_PHARMACY}.pharmacy`);
    const resultStatusMessage = routeResultMapped?.statusMessage?.({
        pharmacySelected: !!pharmacySelected,
        pharmacyMatched:
            pharmacySelected?.secondaryData ===
            getIn(formik.values, `${Sections.RESULTS}.scenario.supplier_npi`),
    });

    const selectedSupplierName = getIn(
        formik.values,
        `${Sections.PATIENT_AND_PHARMACY}.pharmacy`,
    )?.name;

    const supplierName =
        supplierMap?.[getIn(formik.values, `${Sections.RESULTS}.scenario.supplier_npi`)]?.name;

    const eligibleSuppliers = (
        getIn(formik.values, `${Sections.RESULTS}.scenario.live_referral_participants`) ?? []
    ).map((participantNpi) => ({
        supplier_npi: participantNpi,
        supplier_name: supplierMap?.[participantNpi]?.name,
    }));

    const matchingReferralsCount = (
        getIn(formik.values, `${Sections.RESULTS}.matching_referrals`) ?? []
    ).length;

    return (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div
                className="padding__right--small"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    justifyContent: 'space-between',
                }}
            >
                <div>
                    {resultStatusMessage && (
                        <div className="padding__bottom--medium">
                            <InlineMessage
                                label={resultStatusMessage?.label(selectedSupplierName)}
                                intent={resultStatusMessage?.intent}
                            />
                        </div>
                    )}
                    <div className="padding__bottom--medium" style={{ display: 'flex' }}>
                        <div>
                            <DetailLabel label="Route Rule" />
                            <Routing
                                additionalData={{
                                    route_name: getIn(
                                        formik.values,
                                        `${Sections.RESULTS}.scenario.route_name`,
                                    ),
                                    route_description: getIn(
                                        formik.values,
                                        `${Sections.RESULTS}.scenario.route_description`,
                                    ),
                                    internal_note: getIn(
                                        formik.values,
                                        `${Sections.RESULTS}.scenario.internal_note`,
                                    ),
                                }}
                            />
                        </div>
                        <DetailLabelValuePair
                            label="Route Code"
                            value={getIn(formik.values, `${Sections.RESULTS}.scenario.route_code`)}
                            className="padding__left--medium"
                        />
                        <DetailLabelValuePair
                            label="Offer Rate"
                            value={
                                <ServicingRate
                                    servicingRate={getIn(
                                        formik.values,
                                        `${Sections.RESULTS}.scenario.offer_rate`,
                                    )}
                                    referentPriceType={getIn(
                                        formik.values,
                                        `${Sections.RESULTS}.scenario.referent_price_type`,
                                    )}
                                />
                            }
                            className="padding__left--medium"
                        />
                    </div>
                    <PharmacyResults
                        routeResultMapped={routeResultMapped}
                        supplierName={supplierName}
                        eligibleSuppliers={eligibleSuppliers}
                    />
                </div>
                <div>
                    <Button intent={Button.Intent.PRIMARY} label="Start Over" onClick={onClear} />
                </div>
            </div>
            <FormSection
                sectionTitle="Existing Referrals"
                className={classNames('margin__left--small', 'sds_test-referral__referrals-list')}
                content={
                    matchingReferralsCount > 0 ? (
                        <>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'right',
                                }}
                                className="padding__bottom--medium"
                            >
                                {`${matchingReferralsCount} Result${
                                    matchingReferralsCount === 1 ? '' : 's'
                                }`}
                            </div>
                            <Grid
                                data={(
                                    getIn(
                                        formik.values,
                                        `${Sections.RESULTS}.matching_referrals`,
                                    ) ?? []
                                ).map((row) => ({
                                    ...row,
                                    supplier_name: supplierMap?.[row.supplier_npi]?.name,
                                }))}
                                rowCssClass={(rowData) =>
                                    classNames({
                                        'griddle-row--disabled': isReferralRowDisabled(
                                            rowData.referral_status,
                                        ),
                                    })
                                }
                                onRowClick={(rowData) => {
                                    if (!isReferralRowDisabled(rowData.referral_status)) {
                                        navigate(
                                            AppRoutes.referralDetails(
                                                payerIdSelected,
                                                rowData.referral_id,
                                            ),
                                            {
                                                state: {
                                                    backToDestination: {
                                                        route: AppRoutes.testReferral,
                                                        text: 'Back to Test Referral Results',
                                                    },
                                                },
                                            },
                                        );
                                    }
                                }}
                                columns={columns}
                            />
                        </>
                    ) : (
                        <span>No referrals found.</span>
                    )
                }
            />
        </div>
    );
};

ResultsSection.propTypes = {
    onClear: PropTypes.func.isRequired,
};
